import { useEffect, useRef, useState } from 'react';
import { callFn, uploadFile } from '../../lib/api';
import type { Media } from '../../lib/types';
import { useApp } from '../../app/AppContext';
import { useLightbox, ZoomThumb } from '../../components/Lightbox';

export function MediaLibrary() {
  const { toast } = useApp();
  const [media, setMedia] = useState<Media[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const lb = useLightbox();

  async function load() {
    setLoading(true);
    try { setMedia(await callFn<Media[]>('list_media')); }
    catch (e) { toast(e instanceof Error ? e.message : 'Failed', 'err'); }
    finally { setLoading(false); }
  }
  useEffect(() => { void load(); /* eslint-disable-next-line */ }, []);

  async function onUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      for (const f of files) {
        if (!f.type.startsWith('image/')) continue;
        const res = await uploadFile(f);
        await callFn('add_media', { url: res.url, filename: res.filename, content_type: res.contentType, size_bytes: res.sizeBytes });
      }
      toast('Uploaded', 'ok'); void load();
    } catch (err) { toast(err instanceof Error ? err.message : 'Upload failed', 'err'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  }

  async function remove(m: Media) {
    if (!confirm('Remove this image from the library? Items already using it keep their picture; it just leaves the picker.')) return;
    try { await callFn('delete_media', { id: m.id }); toast('Removed', 'ok'); void load(); }
    catch (e) { toast(e instanceof Error ? e.message : 'Failed', 'err'); }
  }

  return (
    <>
      <h1 className="page-title">Media library</h1>
      <p className="page-sub">Images uploaded for items. Upload more here, or pick from these when adding an item.</p>
      <div className="toolbar">
        <button className="btn" onClick={() => fileRef.current?.click()} disabled={uploading}>{uploading ? 'Uploading…' : '+ Upload images'}</button>
        <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={onUpload} />
        <div className="spacer" />
        <span className="page-sub" style={{ margin: 0 }}>{media.length} image{media.length === 1 ? '' : 's'}</span>
      </div>
      {loading ? <div className="loading">Loading…</div>
        : media.length === 0 ? <div className="empty">No images yet — upload some to build your library.</div>
        : (
          <div className="media-grid lib">
            {media.map((m) => (
              <div key={m.id} className="media-card">
                <ZoomThumb src={m.url} alt={m.filename || ''} caption={m.filename || 'image'} block onOpen={lb.open} />
                <div className="media-meta">
                  <span className="media-name" title={m.filename || ''}>{m.filename || 'image'}</span>
                  <button className="btn ghost small" onClick={() => remove(m)}>Remove</button>
                </div>
              </div>
            ))}
          </div>
        )}
      {lb.node}
    </>
  );
}
